/**
 * A MODULE'S ADDRESS IS `/<module>` AND NOTHING AFTER IT
 * (Rachel, 22-10-2026 — `DECISIONS.md`).
 *
 * The shape is `https://dev.pharmos.pcfhk.org/patients`: no further path, no
 * query, no hash. Which record is open and which section is showing stay in
 * memory on that surface. An address that arrives with a query or a hash is
 * replaced by the bare module. A `?tab=` is resolved first (`app/tabs.ts`) and
 * may ride this history entry as `moduleTab` so the open section survives a
 * refresh of the same tab; a copied address does not carry it. Sign-in keeps
 * `?redirect=` — it is not a module.
 */
import { redirect } from "@tanstack/react-router";

declare module "@tanstack/history" {
  interface HistoryState {
    /** The section this history entry opened. Never written into the address. */
    moduleTab?: string;
  }
}

export function moduleTabOf(state: { moduleTab?: unknown } | undefined): string | undefined {
  return typeof state?.moduleTab === "string" ? state.moduleTab : undefined;
}

type ModulePath =
  "/" | "/patients" | "/dispense" | "/pos" | "/inventory" | "/catalogue" | "/reports" | "/settings";

/** Replace a module address that has a query or a hash with the bare module. */
export function replaceWithBareModule(
  to: ModulePath,
  location: { searchStr: string; hash: string },
  state?: { moduleTab?: string },
): void {
  if (location.searchStr === "" && location.hash === "") return;
  throw redirect({
    to,
    search: {},
    hash: "",
    replace: true,
    ignoreBlocker: true,
    ...(state ? { state } : {}),
  });
}
